<template>
  <div class="categories-section">
    <div class="categories-section__wrapper">
      <div v-for="category in filteredCategories" :key="category.key">
        <div
          v-if="!isMobile || !route.params.sottocategoria"
          class="category"
          :class="{ active: isActiveCategory(category.slug, category.key) }"
          @click="toggleCategory(category)"
        >
          <NuxtLink
            class="default-hover text-medium2"
            :to="
              localePath({
                name: pageHandle === 'vinoPage' ? 'vino-slug' : 'birra-slug',
                params: {
                  slug: category.slug,
                },
              })
            "
          >
            <span>{{ category.title }}</span>
          </NuxtLink>
        </div>
        <div
          :class="[
            'subcategories',
            { 'only-subcategories': isMobile && route.params.sottocategoria },
          ]"
          v-show="
            openedCategory === category.key ||
            (isMobile && route.params.sottocategoria)
          "
        >
          <div
            v-for="subcategory in getSubcategories(category.key)"
            :key="subcategory.key"
            :class="{
              active: isActiveSubcategory(subcategory.slug, subcategory.key),
            }"
            class="subcategory"
          >
            <NuxtLink
              class="default-hover text-medium2"
              :to="
                localePath({
                  name:
                    pageHandle === 'vinoPage' ? 'vino-slug-sottocategoria' : '',
                  params: {
                    slug: category.slug,
                    sottocategoria: subcategory.slug,
                  },
                })
              "
            >
              <span>{{ subcategory.title }}</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { parseQueryFacets } from '~/assets/js/utils/algolia.js'
import { useVinoItem } from '~/composables/vino-data.js'
import { useBirraItem } from '~/composables/birra-data.js'

const props = defineProps({
  pageTitle: { type: String, default: '' },
  pageHandle: { type: String, default: '' },
  categories: { type: Array, default: () => [] },
  subcategories: { type: Object, default: () => ({}) },
  facetFilters: { type: Object, default: () => ({ value: [] }) },
  defaultSelected: { type: String, default: '' },
})

const { findVinoItemBySlug, getVinoSubcategories } = useVinoItem(
  props.categories,
  props.subcategories,
)

const { findBirraItemBySlug } = useBirraItem(
  props.categories,
  props.subcategories,
)

// Mobile breakpoint
const isMobile = ref(false)

onMounted(() => {
  isMobile.value = window.innerWidth <= 1000
})

// Stato per la categoria aperta
const openedCategory = ref(null)

// Ottiene le informazioni sulla route corrente
const route = useRoute()

// Funzione per verificare se una categoria è attiva
const isActiveCategory = (slug, key) => {
  return route.params.slug === slug || key === props.defaultSelected
}

// Funzione per verificare se una sottocategoria è attiva
const isActiveSubcategory = (subcategorySlug, subcategoryKey) => {
  return (
    route.params.sottocategoria === subcategorySlug ||
    subcategoryKey === props.defaultSelected
  )
}

// Funzione per aprire/chiudere una categoria
const toggleCategory = (category) => {
  if (isMobile.value) {
    if (openedCategory.value !== category.key) {
      openedCategory.value = category.key
    }
  } else {
    if (isActiveCategory(category.slug)) {
      openedCategory.value =
        openedCategory.value === category.key ? null : category.key
    }
  }
}

// Trova la categoria attuale basata sullo slug nella route
const actualCategory =
  props.pageHandle === 'vinoPage'
    ? findVinoItemBySlug(route.params.slug)
    : findBirraItemBySlug(route.params.slug)

if (actualCategory) {
  // Imposta la chiave della categoria aperta
  openedCategory.value = actualCategory.key
  let subcategory
  // Verifica se c'è una sottocategoria specificata nella route
  if (route.params.sottocategoria) {
    // Trova la sottocategoria corretta basata sul segmento della route
    subcategory = Object.values(props.subcategories)
      .flat()
      .find((subcat) => subcat.slug === route.params.sottocategoria)
  }
  // Aggiorna i filtri
  props.facetFilters.value = parseQueryFacets(
    subcategory ? subcategory.title : actualCategory.title,
  )
}

const getSubcategories = (categoryKey) => {
  return props.pageHandle === 'vinoPage'
    ? getVinoSubcategories(categoryKey)
    : []
}

const filteredCategories = computed(() => {
  // Se non è in mobile, restituisce tutte le categorie
  if (!isMobile.value) {
    return props.categories
  }

  // Estrae il parametro 'sottocategoria' dalla rotta
  const { sottocategoria } = route.params
  // Estrae le categorie dai props
  const { categories } = props
  // Estrae il valore della categoria selezionata
  const { value: openedCategoryKey } = openedCategory

  // Helper per filtrare le sottocategorie in base alla 'sottocategoria' della rotta
  const filterBySubcategory = (category) => ({
    ...category,
    subcategories: getSubcategories(category.key).filter(
      (subcategory) => subcategory.slug === sottocategoria,
    ),
  })

  // Se esiste una 'sottocategoria' nella rotta
  if (sottocategoria) {
    // Filtra le categorie per trovare quella con la chiave della categoria attuale
    // e mappa le sottocategorie in base alla 'sottocategoria' della rotta
    return categories
      .filter((category) => category.key === actualCategory.key)
      .map(filterBySubcategory)
  }

  // Se esiste una categoria aperta
  if (openedCategoryKey) {
    // Trova la categoria selezionata in base alla chiave della categoria aperta
    const selectedCategory = categories.find(
      (category) => category.key === openedCategoryKey,
    )

    // Se la categoria selezionata esiste e non ha sottocategorie, restituisce tutte le categorie
    if (
      selectedCategory &&
      getSubcategories(selectedCategory.key).length === 0
    ) {
      return categories
    }

    // Altrimenti, filtra le categorie per trovare quella con la chiave della categoria aperta
    return categories.filter((category) => category.key === openedCategoryKey)
  }

  // Se nessuna delle condizioni precedenti è soddisfatta, restituisce tutte le categorie
  return categories
})
</script>

<script>
export default {
  name: 'ListingCategories',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
